<template>
  <Layout
    :id="page.id"
    class="onboarding-options"
    :title="page.title"
    :subtitle="page.subtitle"
    :scrollDefaultSlot="true"
  >
    <div
      class="onboarding-options__options"
      :class="{
        'onboarding-options__options--wrap': page.wrapInputs,
      }"
    >
      <template v-for="option in options">
        <OptionCheckbox
          v-if="page.multipleOptions"
          :key="`a-${option.label}`"
          v-model="selectedOptions"
          class="onboarding-options__option"
          :class="{ 'onboarding-options__option--center': page.centerText }"
          :option="option"
          :maxOptions="page.maxOptions"
        />
        <OptionText
          v-else-if="option.type === 'text-input'"
          :key="`b-${option.label}`"
          v-model="selectedOptions"
          class="onboarding-options__option"
          :class="{ 'onboarding-options__option--center': page.centerText }"
          :option="option"
        />
        <OptionRadio
          v-else
          :key="`c-${option.label}`"
          v-model="selectedOptions"
          class="onboarding-options__option"
          :class="{ 'onboarding-options__option--center': page.centerText }"
          :option="option"
        />
      </template>
    </div>

    <button
      v-if="skipOption"
      class="onboarding-options__option-skip"
      @click="skip"
    >
      {{ skipOption.label }}
    </button>

    <img
      v-if="page.image"
      class="onboarding-options__image"
      :src="image"
      :width="page.image.width"
      :height="page.image.height"
      alt=""
    />
  </Layout>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'

import { assertNotNull } from '@/helpers/typing'
import { getImage, QuestionnaireView } from '@/models/onboarding'
import { OptionsPage, QuestionnaireFormOption } from '@/models/onboarding.types'

import Layout from '../Layout.vue'
import OptionRadio from '../OptionRadio.vue'
import OptionText from '../OptionText.vue'
import OptionCheckbox from '../OptionCheckbox.vue'

const props = defineProps<{
  view: QuestionnaireView
}>()

const page = computed(() => {
  return props.view.currentPage as OptionsPage
})

const selectedOptions = ref<string[]>([])

watch(selectedOptions, (newValue) => {
  if (page.value.multipleOptions) {
    props.view.setCurrentChoice(newValue)
  } else {
    // `selectedOptions` is an array to make it easier
    // to handle multiple-option pages, but if this
    // is not a multi-option page, then `selectedOptions`
    // should only have one item.
    props.view.setCurrentChoice(newValue[0])
  }
})

const options = computed(() => {
  // Remove the `skip` option. We handle it differently than the other options.
  return page.value.options.filter((option) => option.type !== 'skip')
})

const skipOption = computed(() => {
  return page.value.options.find(
    (option) => typeof option !== 'string' && option.type === 'skip',
  ) as QuestionnaireFormOption | undefined
})

const image = computed(() => {
  if (!page.value.image) {
    return
  }

  return getImage(page.value.image.src)
})

/**
 * Set `selectedOptions` to the value of the `skipOption`,
 * and navigate to the next page.
 */
async function skip(): Promise<void> {
  selectedOptions.value = [assertNotNull(skipOption.value?.label)]
  // Wait for `watch`.
  await nextTick()

  await props.view.nextPage()
}
</script>

<style lang="scss" scoped>
.onboarding-options {
  :deep(.onboarding-layout__default-slot) {
    display: flex;
    flex-direction: column;
  }

  :deep(.onboarding-layout__text) {
    margin-bottom: 28px;
  }

  &__options {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: auto;
    @media only screen and (min-width: $w-tablet-s) {
      margin-top: unset;
    }
  }

  &__options--wrap {
    flex-direction: row;
    flex-wrap: wrap;

    &:deep(.option-input__label) {
      width: 100%;
      text-align: center;
    }
  }

  &__option {
    flex: 1;
    min-width: fit-content;
  }

  &__option--center {
    :deep(.option-input__label-wrapper) {
      justify-content: center;
    }
  }

  &__option-skip {
    width: 100%;
    padding: 20px;
    margin-bottom: -20px;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: -0.02em;
    text-align: center;
    color: $c-black-3;
    @media only screen and (min-width: $w-tablet-s) {
      margin-top: auto;
    }

    @include hover {
      color: $c-black-15;
    }
  }

  &__image {
    width: auto;
    min-height: 0;
    margin-top: 16px;
    max-height: 100%;
  }
}

#commit .onboarding-options {
  &__options {
    margin-top: 0;
  }

  &__image {
    min-height: 100px;
    margin-left: auto;

    @media only screen and (min-height: 825px) {
      max-height: none;
      margin-top: 48px;
    }
  }
}
</style>
